import React, { useState, useEffect } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./FAQ.css";

interface FAQItem {
  question: string;
  answer: JSX.Element;
}

const faqData = [
  {
    section: "General Questions",
    items: [
      {
        question: "How does The Make a Memory Family fundraising program work?",
        answer: (
          <>
            We provide a simple, high-profit fundraising solution for teams and
            groups. You sign up, launch your campaign, and start raising money
            with no upfront costs.
          </>
        ),
      },
      {
        question: "Who can use your fundraising program?",
        answer: (
          <>
            Any team, group, or organization looking to raise money! Our program
            is perfect for sports teams, schools, and community groups.
          </>
        ),
      },
      {
        question: "How much money can we raise?",
        answer: (
          <>
            Teams can earn <strong>40% - 80% profit</strong> depending on the
            type of product sold during the fundraiser. The more participation,
            the higher the earnings!
          </>
        ),
      },
      {
        question: "Is there a cost to start a fundraiser?",
        answer: (
          <>
            No! There are <strong>no upfront costs </strong>to get started.
          </>
        ),
      },
      {
        question: "How long does a fundraiser last?",
        answer: (
          <>
            Most fundraisers run for <strong>1-2 weeks</strong>, but we can
            customize a plan based on your needs.
          </>
        ),
      },
    ],
  },
  {
    section: "Fundraising Process",
    items: [
      {
        question: "How do we get started?",
        answer: (
          <>
            Simply visit{" "}
            <a href="https://fundraisewithmamfam.com">
              fundraisewithmamfam.com
            </a>{" "}
            and fill out our signup form. Our team will guide you through the
            process!
          </>
        ),
      },
      {
        question: "What types of fundraisers do you offer?",
        answer: (
          <>
            We offer <strong>customized fundraising solutions, </strong>
            including product sales and donation drives.
          </>
        ),
      },
      {
        question: "Can we have a custom online store for our organization?",
        answer: (
          <>
            Yes! You can set up a <strong>custom, branded store</strong> that
            runs alongside your fundraiser, allowing supporters to purchase gear
            and products to support the fundraiser.
          </>
        ),
      },
      {
        question: "Do we need to handle products or inventory?",
        answer: (
          <>
            Nope! Our program is designed to be <strong>hassle-free,</strong> so
            you don’t need to manage inventory or deliveries.
          </>
        ),
      },
    ],
  },
  {
    section: "Payments & Payouts",
    items: [
      {
        question: "How do we collect funds?",
        answer: (
          <>
            All donations and sales are processed online through Square, our
            secure transaction handler. Any funds collected through the brochure
            will be collected by the organization and an invoice will get issued
            at the end of the fundraiser.
          </>
        ),
      },
      {
        question: "When do we receive our earnings?",
        answer: (
          <>
            Payouts are typically sent within <strong>7-14 days</strong> after
            your fundraiser ends.
          </>
        ),
      },
      {
        question: "How do we track our fundraising progress?",
        answer: (
          <>
            You’ll get access to a <strong>real-time dashboard</strong> to
            monitor your earnings and campaign success.
          </>
        ),
      },
    ],
  },
  {
    section: "Support & Contact",
    items: [
      {
        question: "What if we need help during our fundraiser?",
        answer: (
          <>
            Our team is here to support you! You can reach us via{" "}
            <strong>email or phone.</strong>
          </>
        ),
      },
      {
        question: "Can we run multiple fundraisers in a year?",
        answer: (
          <>
            Absolutely! Many teams run <strong>multiple fundraisers</strong>{" "}
            throughout the season to maximize their earnings.
          </>
        ),
      },
      {
        question: "How can we promote our fundraiser?",
        answer: (
          <>
            We provide{" "}
            <strong>
              marketing tips, templates, and social media resources
            </strong>{" "}
            to help you spread the word and maximize your success.
          </>
        ),
      },
    ],
  },
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    document.title = "MamFam FAQ";
  }, []);

  return (
    <div className="faq-container">
      <h1>FAQs</h1>
      <p>
        Find answers to our most frequently asked questions below. If you can't
        find what you're looking for, please contact us and we'll get in touch
        quickly.
      </p>
      <div className="faq-items">
        {faqData.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <h2>{section.section}</h2>
            {section.items.map((item, itemIndex) => {
              const currentIndex = sectionIndex * 100 + itemIndex; // Create a unique index
              return (
                <div key={itemIndex} className="faq-item">
                  <div
                    className="faq-question"
                    onClick={() => toggleFAQ(currentIndex)}
                  >
                    {item.question}
                    <span className="faq-icon">
                      {openIndex === currentIndex ? (
                        <FiChevronUp />
                      ) : (
                        <FiChevronDown />
                      )}
                    </span>
                  </div>
                  {openIndex === currentIndex && (
                    <div className="faq-answer">{item.answer}</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQ;
